import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, CalendarClock, Loader2, XCircle } from "lucide-react";
import { format, parseISO } from "date-fns";
import { nl } from "date-fns/locale";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";

export default function ProposalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/public/proposal/${token}`).then((r) => setData(r.data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || "Voorstel niet gevonden."));
  }, [token]);

  const act = async (kind) => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/public/proposal/${token}/${kind}`);
      setResult(r.status);
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const fmt = (iso) => format(parseISO(iso), "EEEE d MMMM yyyy · HH:mm", { locale: nl });

  return (
    <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center p-4 font-sans">
      <div className="bg-white rounded-3xl border border-[#E8E2D9] shadow-[0_20px_60px_rgb(74,63,63,0.08)] max-w-md w-full p-8">
        <div className="text-center mb-6">
          <div className="w-12 h-12 rounded-full bg-brand text-white flex items-center justify-center mx-auto mb-3 font-display text-lg">NH</div>
          <div className="text-sm text-ink-muted">Nelleke Holt Beauty &amp; Massage</div>
        </div>

        {error && <Center icon={<XCircle className="w-10 h-10 text-brand" />} title="Oeps" text={error} />}

        {!error && !data && <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin text-brand mx-auto" /></div>}

        {!error && data && result === "confirmed" && (
          <Center icon={<CheckCircle2 className="w-12 h-12 text-green-600" />} title="Afspraak bevestigd"
            text="Bedankt! Je afspraak staat nu vast. Je ontvangt een bevestiging per e-mail. Tot snel!" />
        )}
        {!error && data && result === "declined" && (
          <Center icon={<CalendarClock className="w-10 h-10 text-brand" />} title="Doorgegeven"
            text="We hebben genoteerd dat dit voorstel niet past. Neem gerust contact op voor een ander moment." />
        )}

        {!error && data && !result && (
          <div className="text-center">
            <div className="flex justify-center mb-4"><CalendarClock className="w-10 h-10 text-brand" strokeWidth={1.3} /></div>
            <h2 className="text-xl font-display mb-2">Voorstel voor je afspraak</h2>
            <p className="text-sm text-ink-muted mb-5">Beste {data.customer_name}, ik stel de volgende afspraak voor <strong>{data.treatment_name}</strong>.</p>
            {data.status !== "proposal_pending" ? (
              <p className="text-sm text-ink-muted">Dit voorstel is al verwerkt.</p>
            ) : data.expired ? (
              <div className="rounded-xl bg-[#FBEBD2] border border-dashed border-[#E0A24A] text-[#9A5B12] p-4 text-sm" data-testid="proposal-expired">
                Dit voorstel is verlopen. Een voorstel kan tot uiterlijk 24 uur van tevoren geaccepteerd worden. Neem gerust contact op voor een nieuw moment.
              </div>
            ) : (
              <>
                <div className="rounded-xl bg-cream-subtle/60 p-4 text-sm text-left mb-4">
                  <div className="font-medium">{fmt(data.proposed_start)}</div>
                </div>
                {data.comment && (
                  <div className="rounded-xl bg-[#FBF5EC] border-l-[3px] border-brand text-left p-3 text-sm text-ink mb-5" data-testid="proposal-comment">{data.comment}</div>
                )}
                <div className="space-y-2">
                  <Button onClick={() => act("accept")} disabled={busy} data-testid="proposal-accept-btn"
                    className="w-full rounded-full bg-brand hover:bg-brand-hover text-white h-12">Ja, ik accepteer deze afspraak</Button>
                  <Button onClick={() => act("decline")} disabled={busy} variant="outline" data-testid="proposal-decline-btn"
                    className="w-full rounded-full h-11 border-[#E8E2D9]">Nee, past niet</Button>
                </div>
                <p className="text-xs text-ink-muted mt-3">Let op: accepteer minimaal 24 uur van tevoren, anders vervalt het voorstel.</p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Center({ icon, title, text }) {
  return (
    <div className="text-center py-4">
      <div className="flex justify-center mb-4">{icon}</div>
      <h2 className="text-xl font-display mb-2">{title}</h2>
      <p className="text-sm text-ink-muted leading-relaxed">{text}</p>
    </div>
  );
}
